import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Wallet, CreditCard, TrendingUp } from 'lucide-react';
import Alert from '../components/Alert';
import { accountPayableApi, accountReceivableApi, payeeApi, bankAccountApi, paymentMethodApi } from '../services/api';
import { AccountPayable, AccountReceivable, Payee, BankAccount, PaymentMethod } from '../types';

const Home: React.FC = () => {
    const [payables, setPayables] = useState<AccountPayable[]>([]);
    const [receivables, setReceivables] = useState<AccountReceivable[]>([]);
    const [payees, setPayees] = useState<Payee[]>([]);
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const monthStartUtcIso = useMemo(() => {
        const start = new Date(Date.UTC(currentYear, currentMonth, 1, 0, 0, 0, 0));
        return start.toISOString();
    }, [currentMonth, currentYear]);
    const monthEndUtcIso = useMemo(() => {
        const end = new Date(Date.UTC(currentYear, currentMonth + 1, 0, 23, 59, 59, 999));
        return end.toISOString();
    }, [currentMonth, currentYear]);

    const load = useCallback(async () => {
        try {
            setLoading(true);

            // Helper to convert ISO to UTC boundary
            const toUtcBoundary = (iso: string, endOfDay: boolean) => {
                const d = new Date(iso);
                const y = d.getUTCFullYear();
                const m = d.getUTCMonth();
                const day = d.getUTCDate();
                const utc = endOfDay
                    ? new Date(Date.UTC(y, m, day, 23, 59, 59, 999))
                    : new Date(Date.UTC(y, m, day, 0, 0, 0, 0));
                return utc.toISOString();
            };

            const payableParams = {
                dueDateStart: toUtcBoundary(monthStartUtcIso, false),
                dueDateEnd: toUtcBoundary(monthEndUtcIso, true),
            };

            const receivableParams = {
                dueDateStart: toUtcBoundary(monthStartUtcIso, false),
                dueDateEnd: toUtcBoundary(monthEndUtcIso, true),
            };

            const results = await Promise.allSettled([
                accountPayableApi.getAll(payableParams),
                accountReceivableApi.getAll(receivableParams),
                payeeApi.getAll(),
                bankAccountApi.getAll(),
                paymentMethodApi.getAll(),
            ]);
            const [pRes, rRes, payeesRes, bankRes, pmRes] = results;
            if (pRes.status === 'fulfilled') setPayables(pRes.value.data || []); else setPayables([]);
            if (rRes.status === 'fulfilled') setReceivables(rRes.value.data || []); else setReceivables([]);
            if (payeesRes.status === 'fulfilled') setPayees(payeesRes.value.data || []); else setPayees([]);
            if (bankRes.status === 'fulfilled') setBankAccounts(bankRes.value.data || []); else setBankAccounts([]);
            if (pmRes.status === 'fulfilled') setPaymentMethods(pmRes.value.data || []); else setPaymentMethods([]);
            if ([pRes, rRes, payeesRes, bankRes, pmRes].some(r => r.status === 'rejected')) {
                setError('Algumas informações não puderam ser carregadas.');
            } else {
                setError(null);
            }
        } catch (e) {
            setPayables([]);
            setReceivables([]);
            setPayees([]);
            setBankAccounts([]);
            setPaymentMethods([]);
            setError('Não foi possível carregar as informações.');
        } finally {
            setLoading(false);
        }
    }, [monthStartUtcIso, monthEndUtcIso]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const onFocus = () => { load(); };
        const onVisibility = () => { if (document.visibilityState === 'visible') load(); };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [load]);

    const payableTotalBRL = useMemo(() => {
        const sumCents = payables
            .filter(i => i.status === 'pending')
            .reduce((acc, i) => acc + (typeof i.amount === 'number' ? i.amount : 0), 0);
        const reais = (sumCents || 0) / 100;
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(reais || 0);
    }, [payables]);

    const payablePaidBRL = useMemo(() => {
        const sumCents = payables
            .filter(i => i.status === 'paid')
            .reduce((acc, i) => acc + (typeof i.amount === 'number' ? i.amount : 0), 0);
        const reais = (sumCents || 0) / 100;
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(reais || 0);
    }, [payables]);

    const payableOverdueBRL = useMemo(() => {
        const sumCents = payables
            .filter(i => i.status === 'overdue')
            .reduce((acc, i) => acc + (typeof i.amount === 'number' ? i.amount : 0), 0);
        const reais = (sumCents || 0) / 100;
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(reais || 0);
    }, [payables]);

    const receivableTotalBRL = useMemo(() => {
        const sumCents = receivables
            .filter(i => {
                const notReceived = !i.receiptDate; // authoritative guard
                const isOpen = (i.status === 'pending' || i.status === 'overdue') && notReceived;
                return isOpen;
            })
            .reduce((acc, i) => acc + (typeof i.amount === 'number' ? i.amount : 0), 0);
        const reais = (sumCents || 0) / 100;
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(reais || 0);
    }, [receivables]);

    const receivableReceivedBRL = useMemo(() => {
        const sumCents = receivables
            .filter(i => {
                const isReceived = i.status === 'received' || !!i.receiptDate;
                return isReceived;
            })
            .reduce((acc, i) => acc + (typeof i.amount === 'number' ? i.amount : 0), 0);
        const reais = (sumCents || 0) / 100;
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(reais || 0);
    }, [receivables]);

    const receivableOverdueBRL = useMemo(() => {
        const sumCents = receivables
            .filter(i => {
                const isOverdue = i.status === 'overdue' && !i.receiptDate;
                return isOverdue;
            })
            .reduce((acc, i) => acc + (typeof i.amount === 'number' ? i.amount : 0), 0);
        const reais = (sumCents || 0) / 100;
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(reais || 0);
    }, [receivables]);

    const features = [
        {
            title: 'Favorecidos',
            description: 'Gerencie seus favorecidos e categorias',
            icon: Users,
            link: '/payees',
            color: 'bg-blue-500'
        },
        {
            title: 'Contas Bancárias',
            description: 'Controle suas contas e saldos',
            icon: Wallet,
            link: '/bank-accounts',
            color: 'bg-green-500'
        },
        {
            title: 'Métodos de Pagamento',
            description: 'Configure formas de pagamento',
            icon: CreditCard,
            link: '/payment-methods',
            color: 'bg-purple-500'
        },
        {
            title: 'Investimentos',
            description: 'Registrar investimentos e resgates',
            icon: TrendingUp,
            link: '/investments',
            color: 'bg-orange-500'
        }
    ];

    return (
        <div className="space-y-8">
            {/* Welcome Section */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-8 text-white">
                <div className="flex items-center">
                    <TrendingUp className="h-12 w-12 mr-4" />
                    <div>
                        <h1 className="text-3xl font-bold">Bem-vindo ao My Finances</h1>
                        <p className="text-primary-100 mt-2">
                            Gerencie suas finanças de forma simples e eficiente!
                        </p>
                    </div>
                </div>
            </div>

            {/* Summary Current Month - Payables */}
            <div>
                <h2 className="text-lg font-semibold text-secondary-900 mb-3">Contas a Pagar</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Link
                        to={`/accounts-payable?dueDateStart=${encodeURIComponent(monthStartUtcIso)}&dueDateEnd=${encodeURIComponent(monthEndUtcIso)}&status=pending`}
                        className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 hover:shadow-md transition-shadow duration-200"
                    >
                        <div className="text-sm text-secondary-600">A pagar no mês</div>
                        <div className="mt-1 text-2xl font-bold text-secondary-900">{loading ? '—' : payableTotalBRL}</div>
                    </Link>
                    <Link
                        to={`/accounts-payable?dueDateStart=${encodeURIComponent(monthStartUtcIso)}&dueDateEnd=${encodeURIComponent(monthEndUtcIso)}&status=paid`}
                        className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 hover:shadow-md transition-shadow duration-200"
                    >
                        <div className="text-sm text-secondary-600">Pagos no mês</div>
                        <div className="mt-1 text-2xl font-bold text-secondary-900">{loading ? '—' : payablePaidBRL}</div>
                    </Link>
                    <Link
                        to={`/accounts-payable?dueDateStart=${encodeURIComponent(monthStartUtcIso)}&dueDateEnd=${encodeURIComponent(monthEndUtcIso)}&status=overdue`}
                        className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 hover:shadow-md transition-shadow duration-200"
                    >
                        <div className="text-sm text-secondary-600">Atrasados no mês</div>
                        <div className="mt-1 text-2xl font-bold text-secondary-900">{loading ? '—' : payableOverdueBRL}</div>
                    </Link>
                </div>
            </div>

            {/* Summary Current Month - Receivables */}
            <div>
                <h2 className="text-lg font-semibold text-secondary-900 mb-3">Contas a Receber</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Link
                        to={`/accounts-receivable?dueDateStart=${encodeURIComponent(monthStartUtcIso)}&dueDateEnd=${encodeURIComponent(monthEndUtcIso)}&status=pending`}
                        className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 hover:shadow-md transition-shadow duration-200"
                    >
                        <div className="text-sm text-secondary-600">A receber no mês</div>
                        <div className="mt-1 text-2xl font-bold text-secondary-900">{loading ? '—' : receivableTotalBRL}</div>
                    </Link>
                    <Link
                        to={`/accounts-receivable?dueDateStart=${encodeURIComponent(monthStartUtcIso)}&dueDateEnd=${encodeURIComponent(monthEndUtcIso)}&status=received`}
                        className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 hover:shadow-md transition-shadow duration-200"
                    >
                        <div className="text-sm text-secondary-600">Recebidos no mês</div>
                        <div className="mt-1 text-2xl font-bold text-secondary-900">{loading ? '—' : receivableReceivedBRL}</div>
                    </Link>
                    <Link
                        to={`/accounts-receivable?dueDateStart=${encodeURIComponent(monthStartUtcIso)}&dueDateEnd=${encodeURIComponent(monthEndUtcIso)}&status=overdue`}
                        className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 hover:shadow-md transition-shadow duration-200"
                    >
                        <div className="text-sm text-secondary-600">Atrasados no mês</div>
                        <div className="mt-1 text-2xl font-bold text-secondary-900">{loading ? '—' : receivableOverdueBRL}</div>
                    </Link>
                </div>
            </div>

            {error && (<Alert type="warning" title="Aviso" message={error} />)}

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {features.map((feature) => {
                    const Icon = feature.icon;
                    return (
                        <Link
                            key={feature.title}
                            to={feature.link}
                            className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 hover:shadow-md transition-shadow duration-200 group"
                        >
                            <div className="flex items-center mb-4">
                                <div className={`${feature.color} p-3 rounded-lg mr-4`}>
                                    <Icon className="h-6 w-6 text-white" />
                                </div>
                                <h3 className="text-lg font-semibold text-secondary-900 group-hover:text-primary-600">
                                    {feature.title}
                                </h3>
                            </div>
                            <p className="text-secondary-600">{feature.description}</p>
                        </Link>
                    );
                })}
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
                <h2 className="text-xl font-semibold text-secondary-900 mb-4">Resumo Rápido</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{loading ? '—' : payees.length}</div>
                        <div className="text-sm text-blue-600">Favorecidos</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{loading ? '—' : bankAccounts.length}</div>
                        <div className="text-sm text-green-600">Contas Bancárias</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">{loading ? '—' : paymentMethods.length}</div>
                        <div className="text-sm text-purple-600">Métodos de Pagamento</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Home;

